import {
  createCompanyCreatedAction,
  COMPANY_UPDATED,
  COMPANY_DEACTIVATED,
  createGroupCreatedAction,
  GROUP_CREATED,
  GROUP_UPDATED,
  GROUP_DELETED,
  createRoleCreatedAction,
  ROLE_CREATED,
  ROLE_UPDATED,
  ELD_ASSIGNED,
  createEldAssignedAction,
  createEldCreatedAction,
  createEldUpdatedAction,
  ELD_CREATED,
  ELD_UPDATED,
} from "./../actions/appData";
import { ICompanyDefaultHosData } from "./../actions/defaultHosData";
import {
  CargoType,
  Odometer,
  FuelType,
  DriverType,
  TrailerType,
  Ownership,
  Suspension,
  EldDeviceAppData,
  MultiTenancySides,
  TenantAppData,
  HosExceptionAppData,
  CurrentTimeZoneInfoDto,
  RootActionPermissionGroup,
  EventTypeCode,
  StateAppData,
} from "./../../data/service-proxies";
import {
  createAppDataReceivedAction,
  APP_DATA_RECEIVED,
  COMPANY_CREATED,
} from "../actions/appData";

export interface IDropDownAppData<TKey> {
  id?: TKey | null;
  name?: string | null;
}

export interface ICountryAppData extends IDropDownAppData<number> {
  states?: Array<StateAppData> | null;
  dutyCycles?: Array<IDropDownAppData<number>> | null;
}

export interface ICompanyAppData extends IDropDownAppData<number> {
  defaultHosData?: Omit<ICompanyDefaultHosData, "companyId">;
}

export interface IAppDataDictionary<TValue> {
  [key: string]: TValue;
}

export interface IAppData {
  countries: Array<ICountryAppData>;
  timeZones?: Array<IDropDownAppData<string>> | null;
  cargoTypes?: Array<CargoType> | null;
  odometerTypes?: Array<Odometer> | null;
  cycleRules?: Array<IDropDownAppData<number>> | null;
  driverTypes?: Array<DriverType> | null;
  dutyStatuses?: Array<string> | null;
  companies: Array<ICompanyAppData>;
  roles?: Array<IDropDownAppData<string>> | null;
  groups?: Array<IDropDownAppData<number>> | null;
  knownDocumentTypes?: IAppDataDictionary<
    Array<IDropDownAppData<number>>
  > | null;
  fuelTypes?: Array<FuelType> | null;
  trailerOwnershipTypes?: Array<Ownership> | null;
  trailerSuspensionTypes?: Array<Suspension> | null;
  trailerTypes?: Array<TrailerType> | null;
  violationTypes?: Array<string> | null;
  actionPermissions?: RootActionPermissionGroup[] | null | undefined;
  fieldPermissions?:
    | {
        [key: string]: {
          [key: string]: any;
        };
      }
    | null
    | undefined;
  eldDevices?: EldDeviceAppData[] | null;
  databaseServers?: string[] | null;
  tenants?: Array<IDropDownAppData<string>> | null;
  multiTenancySide?: MultiTenancySides | null;
  tenant?: TenantAppData | null;
  apiUrl?: string | null;
  webAppUrl?: string | null;
  hosExceptions?: { [key: string]: Array<HosExceptionAppData> } | null;
  timeZoneInfo?: CurrentTimeZoneInfoDto | null;
  sensorAttributes?: Array<string>;
  eventTypeCodes: { [key: string]: EventTypeCode };
}

export const initialAppData: IAppData = {
  countries: [],
  timeZones: [],
  cargoTypes: [],
  odometerTypes: [],
  cycleRules: [],
  driverTypes: [],
  dutyStatuses: [],
  companies: [],
  knownDocumentTypes: {},
  roles: [],
  groups: [],
  fuelTypes: [],
  trailerOwnershipTypes: [],
  trailerSuspensionTypes: [],
  trailerTypes: [],
  violationTypes: [],
  actionPermissions: [],
  fieldPermissions: {},
  eldDevices: [],
  databaseServers: [],
  tenants: [],
  timeZoneInfo: {},
  sensorAttributes: [],
  eventTypeCodes: {},
};

const isCompanyCreatedAction = (
  action:
    | ReturnType<typeof createAppDataReceivedAction>
    | ReturnType<typeof createCompanyCreatedAction>
    | ReturnType<typeof createGroupCreatedAction>
    | ReturnType<typeof createRoleCreatedAction>
    | ReturnType<typeof createEldAssignedAction>
): action is ReturnType<typeof createCompanyCreatedAction> => {
  return (
    "id" in action.payload &&
    "name" in action.payload &&
    "defaultHosData" in action.payload
  );
};

const isEldAssignedAction = (
  action:
    | ReturnType<typeof createAppDataReceivedAction>
    | ReturnType<typeof createCompanyCreatedAction>
    | ReturnType<typeof createGroupCreatedAction>
    | ReturnType<typeof createRoleCreatedAction>
    | ReturnType<typeof createEldAssignedAction>
): action is ReturnType<typeof createEldAssignedAction> => {
  return "id" in action.payload && "assigned" in action.payload;
};

const isEldDeviceAction = (
  action:
    | ReturnType<typeof createAppDataReceivedAction>
    | ReturnType<typeof createCompanyCreatedAction>
    | ReturnType<typeof createGroupCreatedAction>
    | ReturnType<typeof createRoleCreatedAction>
    | ReturnType<typeof createEldCreatedAction>
    | ReturnType<typeof createEldUpdatedAction>
    | ReturnType<typeof createEldAssignedAction>
): action is ReturnType<typeof createEldCreatedAction> => {
  return "id" in action.payload && "serialNumber" in action.payload;
};

const isGroupCreatedAction = (
  action:
    | ReturnType<typeof createAppDataReceivedAction>
    | ReturnType<typeof createCompanyCreatedAction>
    | ReturnType<typeof createGroupCreatedAction>
    | ReturnType<typeof createRoleCreatedAction>
    | ReturnType<typeof createEldCreatedAction>
    | ReturnType<typeof createEldUpdatedAction>
    | ReturnType<typeof createEldAssignedAction>
): action is ReturnType<typeof createGroupCreatedAction> => {
  return "id" in action.payload && "name" in action.payload;
};

const isRoleCreatedAction = (
  action:
    | ReturnType<typeof createAppDataReceivedAction>
    | ReturnType<typeof createCompanyCreatedAction>
    | ReturnType<typeof createGroupCreatedAction>
    | ReturnType<typeof createRoleCreatedAction>
    | ReturnType<typeof createEldCreatedAction>
    | ReturnType<typeof createEldUpdatedAction>
    | ReturnType<typeof createEldAssignedAction>
): action is ReturnType<typeof createRoleCreatedAction> => {
  return (
    "id" in action.payload &&
    "name" in action.payload &&
    typeof action.payload.id === "string"
  );
};

export const appDataReducer = (
  previousState: IAppData = initialAppData,
  action:
    | ReturnType<typeof createAppDataReceivedAction>
    | ReturnType<typeof createCompanyCreatedAction>
    | ReturnType<typeof createGroupCreatedAction>
    | ReturnType<typeof createEldCreatedAction>
    | ReturnType<typeof createEldUpdatedAction>
    | ReturnType<typeof createEldAssignedAction>
): IAppData => {
  switch (action.type) {
    case ELD_ASSIGNED:
      if (!isEldAssignedAction(action)) {
        return previousState;
      }

      const changedEldDevice = previousState.eldDevices?.find(
        (x) => x.id === action.payload.id
      );

      if (!changedEldDevice) {
        return previousState;
      }

      return {
        ...previousState,
        eldDevices: [
          ...previousState.eldDevices!.filter(
            (x) => x.id !== changedEldDevice.id
          ),
          { ...changedEldDevice, assigned: action.payload.assigned },
        ],
      };

    case ELD_CREATED:
      if (!isEldDeviceAction(action)) {
        return previousState;
      }

      const existing = previousState.eldDevices?.find(
        (x) => x.id === action.payload.id
      );

      if (existing) {
        return previousState;
      }

      const devices = previousState.eldDevices ?? [];
      const sorted = [
        ...devices,
        {
          id: action.payload.id,
          serialNumber: action.payload.serialNumber,
          assigned: false,
        },
      ].sort((left, right) => {
        const leftNormalized = left.serialNumber?.toLowerCase() ?? "";
        const rightNormalized = right.serialNumber?.toLowerCase() ?? "";
        if (leftNormalized < rightNormalized) {
          return -1;
        }

        if (leftNormalized > rightNormalized) {
          return 1;
        }

        return 0;
      });

      return {
        ...previousState,
        eldDevices: sorted,
      };

    case ELD_UPDATED:
      if (!isEldDeviceAction(action)) {
        return previousState;
      }

      const existingEld = previousState.eldDevices?.find(
        (x) => x.id === action.payload.id
      );
      if (!existingEld) {
        return previousState;
      }

      let previousDevices = previousState.eldDevices ?? [];
      previousDevices = [
        ...previousDevices.filter((x) => x.id !== existingEld.id),
        {
          id: action.payload.id,
          serialNumber: action.payload.serialNumber,
          assigned: existingEld.assigned,
        },
      ].sort((left, right) => {
        const leftNormalized = left.serialNumber?.toLowerCase() ?? "";
        const rightNormalized = right.serialNumber?.toLowerCase() ?? "";
        if (leftNormalized < rightNormalized) {
          return -1;
        }

        if (leftNormalized > rightNormalized) {
          return 1;
        }

        return 0;
      });

      return {
        ...previousState,
        eldDevices: previousDevices,
      };

    case APP_DATA_RECEIVED:
      if (isCompanyCreatedAction(action)) {
        return previousState;
      }

      return {
        ...previousState,
        ...action.payload,
      };

    case COMPANY_CREATED:
      if (!isCompanyCreatedAction(action)) {
        return previousState;
      }

      return {
        ...previousState,
        companies: [
          ...(previousState.companies as ICompanyAppData[]),
          action.payload,
        ],
      };

    case COMPANY_UPDATED:
      if (!isCompanyCreatedAction(action)) {
        return previousState;
      }

      return {
        ...previousState,
        companies: [
          ...(previousState.companies as ICompanyAppData[]).filter(
            (x) => x.id !== action.payload.id
          ),
          action.payload,
        ],
      };

    case COMPANY_DEACTIVATED:
      if (!isCompanyCreatedAction(action)) {
        return previousState;
      }

      return {
        ...previousState,
        companies: [
          ...(previousState.companies as ICompanyAppData[]).filter(
            (x) => x.id !== action.payload.id
          ),
        ],
      };

    case GROUP_CREATED:
      if (isCompanyCreatedAction(action)) {
        return previousState;
      }

      if (isRoleCreatedAction(action)) {
        return previousState;
      }

      if (!isGroupCreatedAction(action)) {
        return previousState;
      }

      return {
        ...previousState,
        groups: [
          ...(previousState.groups as IDropDownAppData<number>[]),
          action.payload,
        ],
      };

    case GROUP_UPDATED:
      if (isCompanyCreatedAction(action)) {
        return previousState;
      }

      if (isRoleCreatedAction(action)) {
        return previousState;
      }

      if (!isGroupCreatedAction(action)) {
        return previousState;
      }

      return {
        ...previousState,
        groups: [
          ...(previousState.groups as IDropDownAppData<number>[]).filter(
            (x) => x.id !== action.payload.id
          ),
          action.payload,
        ],
      };

    case GROUP_DELETED:
      if (isCompanyCreatedAction(action)) {
        return previousState;
      }

      if (isRoleCreatedAction(action)) {
        return previousState;
      }

      if (!isGroupCreatedAction(action)) {
        return previousState;
      }

      return {
        ...previousState,
        groups: [
          ...(previousState.groups as IDropDownAppData<number>[]).filter(
            (x) => x.id !== action.payload.id
          ),
        ],
      };

    case ROLE_CREATED:
      if (!isRoleCreatedAction(action)) {
        return previousState;
      }

      return {
        ...previousState,
        roles: [
          ...(previousState.roles as IDropDownAppData<string>[]),
          { id: action.payload.id, name: action.payload.name },
        ],
      };

    case ROLE_UPDATED:
      if (!isRoleCreatedAction(action)) {
        return previousState;
      }

      return {
        ...previousState,
        roles: [
          ...(previousState.roles as IDropDownAppData<string>[]).filter(
            (x) => x.id !== action.payload.id
          ),
          { id: action.payload.id, name: action.payload.name },
        ],
      };

    default:
      return previousState;
  }
};
